import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ShieldCheck, Sparkles, Users } from 'lucide-react';

const clients = [
  { id: 1, name: 'VLCC Clinic', field: 'Wellness & Aesthetics', logo: '/assets/logos/1.png' },
  { id: 2, name: 'Wonder Alu Board', field: 'Aluminium Panels & Building Materials', logo: '/assets/logos/2.png' },
  { id: 3, name: 'Samasta Foods', field: 'Food & FMCG', logo: '/assets/logos/3.png' },
  { id: 4, name: 'The HR Scoop (JBPL)', field: 'HR Media & Insights', logo: '/assets/logos/4.png' },
  { id: 5, name: 'Truefitt & Hill', field: 'Luxury Grooming & Barbershop', logo: '/assets/logos/5.png' },
  { id: 6, name: 'OBO Bettermann', field: 'Electrical Infrastructure', logo: '/assets/logos/6.png' },
  { id: 7, name: 'Jamna Auto Industries Ltd.', field: 'Automotive Components', logo: '/assets/logos/7.png' },
  { id: 8, name: 'Kewal Kiran Clothing Ltd.', field: 'Fashion & Apparel', logo: '/assets/logos/8.png' },
  { id: 9, name: 'Trident Group', field: 'Manufacturing & Textiles', logo: '/assets/logos/9.png' },
  { id: 10, name: 'O21 Mall | Malhar Mega Mall', field: 'Retail & Shopping Mall', logo: '/assets/logos/10.png' },
  { id: 11, name: 'Anjushree', field: 'Hospitality', logo: '/assets/logos/11.png' },
  { id: 12, name: 'Deve Ka Divya', field: 'Consumer Brand', logo: '/assets/logos/12.png' },
  { id: 13, name: 'Black Nutrition', field: 'Sports Nutrition', logo: '/assets/logos/13.png' },
  { id: 14, name: 'Integriti', field: 'Professional Services', logo: '/assets/logos/14.png' },
  { id: 15, name: 'Lawman Pg3', field: 'Fashion & Lifestyle', logo: '/assets/logos/15.png' },
];

const ClientsSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const singleSetRef = useRef(null);
  const [singleSetWidth, setSingleSetWidth] = useState(0);

  useEffect(() => {
    if (!singleSetRef.current) {
      return;
    }

    const measure = () => {
      if (singleSetRef.current) {
        setSingleSetWidth(singleSetRef.current.scrollWidth);
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(singleSetRef.current);

    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      resizeObserver.disconnect();
    };
  }, []);

  const renderClientCard = (client, rowPrefix) => (
    <div
      key={`${rowPrefix}-${client.id}`}
      className="card-elevated flex-shrink-0 w-[152px] sm:w-[180px] lg:w-[210px] p-3 sm:p-4 text-center"
      data-testid={`client-marquee-${rowPrefix}-${client.id}`}
    >
      <div className="rounded-xl bg-white border border-purple-100 h-[92px] sm:h-[108px] lg:h-[122px] flex items-center justify-center mb-3">
        <img
          src={client.logo}
          alt={`${client.name} logo`}
          className="h-16 sm:h-20 lg:h-24 w-auto object-contain"
          loading="lazy"
        />
      </div>
      <h3 className="text-xs sm:text-sm lg:text-base font-semibold text-gray-800 leading-tight truncate">{client.name}</h3>
      <p className="text-[10px] sm:text-xs text-gray-500 truncate mt-1">{client.field}</p>
    </div>
  );

  const rowOneDuration = singleSetWidth > 0 ? Math.max(24, singleSetWidth / 82) : 28;
  const rowTwoDuration = singleSetWidth > 0 ? Math.max(28, singleSetWidth / 68) : 34;

  return (
    <section className="relative py-24 section-light overflow-hidden" data-testid="clients-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="inline-block text-sm uppercase tracking-wider font-bold text-purple-700 bg-purple-100 px-4 py-2 rounded-full mb-4">
            Trusted Partnerships
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            Brands We Help <span className="gradient-text">Scale And Lead</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            A moving wall of partner brands that trust Grawish for strategy, storytelling,
            and measurable growth.
          </p>
        </motion.div>

        <div className="relative">
          <motion.div
            className="flex w-max py-2 [will-change:transform] [backface-visibility:hidden] [transform:translate3d(0,0,0)]"
            animate={
              shouldReduceMotion || singleSetWidth === 0
                ? { x: 0 }
                : { x: [0, -singleSetWidth] }
            }
            transition={{ duration: rowOneDuration, repeat: Infinity, ease: 'linear' }}
            aria-label="Client marquee row one"
          >
            <div ref={singleSetRef} className="flex gap-3 sm:gap-4 lg:gap-5 pr-3 sm:pr-4 lg:pr-5">
              {clients.map((client) => renderClientCard(client, 'top-a'))}
            </div>
            <div className="flex gap-3 sm:gap-4 lg:gap-5" aria-hidden="true">
              {clients.map((client) => renderClientCard(client, 'top-b'))}
            </div>
          </motion.div>

          <motion.div
            className="flex w-max py-2 mt-4 sm:mt-5 opacity-90 [will-change:transform] [backface-visibility:hidden] [transform:translate3d(0,0,0)]"
            animate={
              shouldReduceMotion || singleSetWidth === 0
                ? { x: 0 }
                : { x: [-singleSetWidth, 0] }
            }
            transition={{ duration: rowTwoDuration, repeat: Infinity, ease: 'linear' }}
            aria-label="Client marquee row two"
          >
            <div className="flex gap-3 sm:gap-4 lg:gap-5 pr-3 sm:pr-4 lg:pr-5">
              {clients.map((client) => renderClientCard(client, 'bottom-a'))}
            </div>
            <div className="flex gap-3 sm:gap-4 lg:gap-5" aria-hidden="true">
              {clients.map((client) => renderClientCard(client, 'bottom-b'))}
            </div>
          </motion.div>
        </div>

        <motion.div
          className="mt-12 flex flex-wrap justify-center gap-4 sm:gap-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-full border border-purple-100 shadow-sm">
            <Users className="w-4 h-4 text-purple-600" />
            <span className="font-medium">100+ Active Brand Partnerships</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-full border border-purple-100 shadow-sm">
            <ShieldCheck className="w-4 h-4 text-purple-600" />
            <span className="font-medium">Long-Term Growth Focused Teams</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700 bg-white px-4 py-2 rounded-full border border-purple-100 shadow-sm">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="font-medium">Creative + Performance Led Execution</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ClientsSection;
